// Общие примитивы договора. Форматы ID — канон §16 и tech/02 §1.
import { z } from 'zod';

/** Строгий объект: лишнее поле — ошибка (ADR-0003 п. 1, E01-T01 «лишнее поле → 400»). */
export const strict = z.strictObject;

/** UUID v7 (tech/02 §1: бои, заказы, инстансы, сессии; tech/03 §1: Idempotency-Key). */
export const Uuid7 = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, 'ожидается UUID v7 в нижнем регистре');
export type Uuid7 = z.infer<typeof Uuid7>;

/** Время в ответах — ISO 8601 UTC (tech/03 §1 «Время»). */
export const IsoTime = z.iso.datetime();
/** Метка времени в мс — там, где протокол боя считает сроки в мс (tech/04 §3, §4.3). */
export const EpochMs = z.int().nonnegative();

/** Публичный ID игрока: 8 символов base32 без похожих букв (tech/02 §3, players.public_id). */
export const PublicId = z.string().regex(/^[0-9A-HJ-NP-Z]{8}$/);
/** bigint из PG наружу — строкой (tech/08 §2 п. 10, Т-К-24). */
export const BigIdString = z.string().regex(/^[1-9][0-9]{0,18}$/);
export const Hex64 = z.string().regex(/^[0-9a-f]{64}$/);
export const Hex32 = z.string().regex(/^[0-9a-f]{32}$/);

// --- ID канона (канон §16, tech/02 §1). Форматы допускают D11+, B13+ — новые приходят контентом без релиза. ---
export const DistrictId = z.string().regex(/^D[0-9]{1,3}$/);
/** Босс района B01…, звезда сезона B901+, мировой W01; `B01-T` — учебная Шушера (ГД 06 §3, ГД 12 T03). */
export const BossId = z.string().regex(/^(B[0-9]{2,4}(-T)?|W[0-9]{2,3})$/);
/** ID приёма босса `<босс>-M<n>` (ГД 06 §4, А1-050). */
export const BossMoveId = z.string().regex(/^(B[0-9]{2,4}(-T)?|W[0-9]{2,3})-M[0-9]{1,2}$/);
export const ItemDefId = z.string().regex(/^EQ-(HEAD|JACKET|PANTS|SHOES|TOOL|ACC)-[0-9]{2,3}$/);
export const SetId = z.string().regex(/^S[0-9]{1,2}$/);
export const ZoneId = z.string().regex(/^G(0[1-9]|10)$/);
/** Шаг обучения T01…T10 или `done` (tech/02 §3 players.tutorial_step). */
export const TutorialStep = z.string().regex(/^T(0[1-9]|10)$|^done$/);
/** ID товара магазина — значение конфига `shop.catalog`, не перечисление в коде (решение каркаса Р-07). */
export const ProductId = z.string().regex(/^[A-Z0-9][A-Z0-9-]{2,63}$/);
/** Ключ стопки инвентаря: 'BOX-WOOD', 'THERMOS', 'PART-ENGINE-01' (tech/02 §5). */
export const StackDefId = z.string().regex(/^[A-Z0-9][A-Z0-9-]{1,63}$/);

export const Slot = z.enum(['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory']);
export type Slot = z.infer<typeof Slot>;
export const Rarity = z.enum(['common', 'good', 'rare', 'epic', 'legendary']);
export type Rarity = z.infer<typeof Rarity>;

/** 13 кодов валют журнала (tech/02 §4 ledger_accounts.currency, economy/01 §2). */
export const CurrencyCode = z.enum([
  'energy', 'nuts', 'diesel', 'talons', 'keys', 'tickets', 'scrap', 'master_xp',
  'build_pts', 'marks', 'season_pts', 's8_tokens', 'event_tokens',
]);
export type CurrencyCode = z.infer<typeof CurrencyCode>;

/** Пагинация курсором (tech/03 §1 «Пагинация»): `?cursor=<opaque>&limit=≤50`. */
export const PageQuery = strict({
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export const NextCursor = z.string().max(256).nullable();

/** Состав выдачи (товар, награда шага). Числа — из конфига экономики; форма — решение каркаса Р-09. */
/** Косметика и бейджи без статов (tech/02 `cosmetics_owned`): `C-CHAR-03`, `BADGE-CHAIRMAN`. */
export const CosmeticId = z.string().regex(/^[A-Z][A-Z0-9-]{1,63}$/);

export const Grant = strict({
  currencies: z.partialRecord(CurrencyCode, z.int().positive()).optional(),
  items: z.array(strict({ defId: z.union([ItemDefId, StackDefId]), qty: z.int().positive() })).optional(),
  /** Косметика события и бейдж «Председательского заказа» (Р-109). */
  cosmetics: z.array(CosmeticId).optional(),
});
export type Grant = z.infer<typeof Grant>;

export const Empty = strict({});
